import { useQuery } from '@tanstack/react-query';
import { Device, LocationInfo, WebSocketService, locationTransformer, OutdoorGeofencingMode } from '@ixicare/resources';
import React from 'react';
import { useProfileConfiguration } from '~utils/profile';

export function useLocation(deviceId?: number, initialData?: LocationInfo) {
  const { data: lastDeviceLocation } = useQuery({
    queryKey: ['device-location', deviceId],
    queryFn: () => Device.getDeviceLastLocation(deviceId),
    enabled: deviceId !== undefined && !initialData,
  });
  const [data, setData] = React.useState<LocationInfo | undefined>(initialData);

  React.useEffect(() => {
    if (lastDeviceLocation) setData((curr) => (curr ? curr : (lastDeviceLocation?.lastKnown ?? undefined)));
  }, [lastDeviceLocation]);

  const url = `/location-streaming/${deviceId}?use_updated_schemas=true`;

  const socket = React.useMemo(() => (url ? new WebSocketService<LocationInfo>(url, setData) : undefined), [url]);

  React.useEffect(() => {
    socket?.open();
    return () => socket?.forceDisconnect();
  }, [url]);

  return data;
}

export function useGPSGeofence(residentId?: number) {
  const profileConfig = useProfileConfiguration(residentId ?? -1);
  if (profileConfig.data && profileConfig.data['outdoor-geofencing-mode'] === OutdoorGeofencingMode.Geo) {
    return profileConfig.data['outdoor-geofencing-geo'];
  }
  return undefined;
}

// Helper for debugging issues, use this instead of `useLocation` and use the steps from the app logs.
export function useLocationPlayback(): LocationInfo {
  const locations: any[] = [
    {
      _discriminator: 'OUTDOOR',
      accuracy: 471,
      coords: {
        srid: 4326,
        x: 77.57716369628906,
        y: 13.008087158203125,
      },
      description: null,
      latitude: 13.008087158203125,
      longitude: 77.57716369628906,
      timestamp: '2024-02-27T07:26:59.363188',
    },
    {
      _discriminator: 'INDOOR',
      accuracy: null,
      coords: {
        _transformations: [
          {
            srid: 990000,
            x: null,
            y: null,
          },
        ],
        x: null,
        y: null,
      },
      description: '0 (new), Office Room',
      floor_number: 0,
      pixels: {
        x: null,
        y: null,
      },
      plane_id: 155,
      plane_uuid: 'eebf29ba-eede-49c6-a515-9ee7b33749bc',
      room_number: 'Office Room',
      site_id: 204,
      site_uuid: 'b9f60c31-d089-459f-9d33-aea7479f4432',
      timestamp: '2024-02-27T07:27:47',
      x: null,
      y: null,
      zone_id: '87db453a-5927-43e7-ac94-e40018f2f049',
    },
  ].map(locationTransformer);
  const [counter, setCounter] = React.useState(0);
  React.useEffect(() => {
    const interval = setInterval(() => setCounter((x) => x + 1), 10e3);
    return () => clearInterval(interval);
  }, []);
  return locations[counter % locations.length];
}
